/**
 * Shared last-activity timestamp for the staff-portal idle timer (#2212).
 *
 * The zustand auth store's `lastActivityAt` was in-memory only, so a reload
 * restarted the 30 minutes and every tab kept its own clock. This helper is
 * the single clock for the origin: localStorage so tabs and reloads agree,
 * plus an in-memory fallback when the accessor itself throws (private window,
 * blocked site data).
 */

export const IDLE_CLOCK_STORAGE_KEY = 'staff-portal-idle-clock';

let memoryLastActivityAt: number | null = null;

function parseTimestamp(raw: string): number | null {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || String(parsed) !== raw) return null;
  return parsed;
}

export function readIdleClock(): number | null {
  try {
    const raw = localStorage.getItem(IDLE_CLOCK_STORAGE_KEY);
    if (raw === null) return memoryLastActivityAt;
    const parsed = parseTimestamp(raw);
    if (parsed === null) return memoryLastActivityAt;
    memoryLastActivityAt = parsed;
    return parsed;
  } catch {
    return memoryLastActivityAt;
  }
}

export function writeIdleClock(timestamp: number): void {
  memoryLastActivityAt = timestamp;
  try {
    localStorage.setItem(IDLE_CLOCK_STORAGE_KEY, String(timestamp));
  } catch {
    // Keep the in-memory clock; the page must still run.
  }
}

export function clearIdleClock(): void {
  memoryLastActivityAt = null;
  try {
    localStorage.removeItem(IDLE_CLOCK_STORAGE_KEY);
  } catch {
    // Memory is already cleared; nothing else we can do.
  }
}
